import { defineStore } from 'pinia'
import axios from 'axios'
import { shouldClearSessionOnHttpError } from '@/utils/httpAuth'
import { clearFreeMealPlanDraft, clearPendingOAuthMealPlanSubmit } from '@/utils/freeMealPlan/draftStorage'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null, token: null, avatarUploading: false, originalUrl: null,
  }), getters: {
    isAuthenticated: (state) => !!state.token, avatarUrl: (state) => {
      if (state.user && state.user.avatar) {
        return state.user.avatar
      }
      return null
    }
  }, actions: {
    initialize () {
      if (this.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`
      }
    },

    saveOriginalUrl (url) {
      this.originalUrl = url
    },

    getOriginalUrl () {
      const url = this.originalUrl
      // Clear the stored URL after retrieving it
      this.originalUrl = null
      return url
    },

    async register ({ email, password }) {
      const response = await axios.post('/api/register', {
        email, password
      })
      this.user = response.data.user
      this.token = response.data.token
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`
    },

    async login ({ email, password, remember }) {
      const response = await axios.post('/api/login', {
        email, password, remember
      })
      this.user = response.data.user
      this.token = response.data.token
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`
    },

    /**
     * @param {{ redirectPath?: string }} [options] - After OAuth, send users here (path only, e.g. /ai-diet-planner).
     */
    async loginWithGoogle (options = {}) {
      const { redirectPath } = options
      try {
        if (redirectPath) {
          this.saveOriginalUrl(redirectPath)
          try {
            sessionStorage.setItem('yumnia-oauth-return', redirectPath)
          } catch {
            // ignore
          }
        }

        // Determine the platform (web, ios, or android)
        let platform = 'web'
        const isMobileApp = window.Capacitor && window.Capacitor.isNativePlatform()

        if (isMobileApp) {
          platform = window.Capacitor.getPlatform() // 'ios' or 'android'
        }

        let url = `/api/auth/google?mobile=${isMobileApp ? 'true' : 'false'}&platform=${platform}`
        if (redirectPath) {
          url += `&redirect=${encodeURIComponent(redirectPath)}`
        }

        const response = await axios.get(url)
        window.location.href = response.data.url
      } catch (error) {
        console.error('Google login error:', error)
        throw error
      }
    },

    async loginWithApple () {
      try {
        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')

        const options = {
          scopes: 'email name',
        }

        const result = await SignInWithApple.authorize(options)

        const identityToken = result.response.identityToken
        const appleId = result.response.user
        const email = result.response.email
        const givenName = result.response.givenName
        const familyName = result.response.familyName

        let fullName = null
        if (givenName || familyName) {
          fullName = `${givenName || ''} ${familyName || ''}`.trim()
        }

        let platform = 'web'
        if (window.Capacitor && window.Capacitor.isNativePlatform()) {
          platform = window.Capacitor.getPlatform()
        }

        const response = await axios.post('/api/auth/apple', {
          identityToken,
          apple_id: appleId,
          email: email,
          name: fullName,
          platform
        })

        this.user = response.data.user
        this.token = response.data.token
        axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`

        return response.data
      } catch (error) {
        console.error('Apple login error:', error)
        throw error
      }
    },

    async setToken (token) {
      if (token) {
        token = decodeURIComponent(token)
        token = token.replace(/%23/g, '#')
        token = token.trim()
        token = token.replace(/#$/, '')
      }

      this.token = token
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      try {
        await this.fetchUser()
        return this.user
      } catch (error) {
        console.error('❌ Error fetching user after setting token:', error)
        throw error
      }
    },

    async tryRestoreSessionFromToken (token) {
      try {
        await this.setToken(token)
        return true
      } catch {
        this.resetSessionState()
        return false
      }
    },

    async fetchUser () {
      if (!this.token) {
        return null
      }

      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`
        const response = await axios.get('/api/user')
        this.user = response.data
        return this.user
      } catch (error) {
        console.error('❌ Error fetching user:', error)
        if (error.response && shouldClearSessionOnHttpError(error)) {
          console.warn('🔒 Token unauthorized or session invalid, clearing auth state')
          this.resetSessionState()
        }
        throw error
      }
    },

    async logout () {
      try {
        await axios.post('/api/logout', {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
      } catch (error) {
        console.error('Logout API error:', error)
      } finally {
        this.resetSessionState()
      }
    },

    /**
     * Tear down all client-side session state. Also clears session-scoped
     * meal-plan caches so the next user (or a guest) never sees the previous
     * account's pre-filled wizard answers.
     */
    resetSessionState () {
      this.user = null
      this.token = null
      delete axios.defaults.headers.common['Authorization']
      clearFreeMealPlanDraft()
      clearPendingOAuthMealPlanSubmit()
    },

    async uploadAvatar (file) {
      if (!this.token || !file) return

      const formData = new FormData()
      formData.append('avatar', file)

      this.avatarUploading = true

      try {
        const response = await axios.post('/api/avatar/upload', formData, {
          timeout: 20000,
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'multipart/form-data',
          }
        })

        if (this.user) {
          this.user.avatar = response.data.avatar
        }

        return response.data
      } catch (error) {
        console.error('Avatar upload error:', error)
        throw error
      } finally {
        this.avatarUploading = false
      }
    },

    async deleteAvatar () {
      if (!this.token) return

      try {
        await axios.delete('/api/avatar/delete', {
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        if (this.user) {
          this.user.avatar = null
        }

        return { success: true }
      } catch (error) {
        console.error('Avatar delete error:', error)
        throw error
      }
    },

    async updateProfile (profileData) {
      if (!this.token) return

      try {
        const response = await axios.put('/api/profile', profileData, {
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        // Update the user data in the store
        if (this.user) {
          // Always update the name
          this.user.name = profileData.name

          // Only update the email if no verification is required
          // If email verification was sent, the backend will not have updated the email yet
          if (!response.data.email_verification_sent) {
            this.user.email = profileData.email
          }
        }

        return response.data
      } catch (error) {
        console.error('Profile update error:', error)
        throw error
      }
    },

    async verifyEmailChange (token, email) {
      try {
        const response = await axios.post('/api/verify-email-change', {
          token, email
        })

        // If verification was successful, update the user's email in the store
        if (response.data.verified && this.user && this.token) {
          this.user.email = email

          // Refresh the user data from the server
          await this.fetchUser()
        }

        return response.data
      } catch (error) {
        console.error('Email verification error:', error)
        throw error
      }
    },

    async verifyEmail (token, email) {
      try {
        const response = await axios.post('/api/verify-email', {
          token, email
        })

        // If verification was successful and user is logged in, refresh user data
        if (response.data.verified && this.user && this.token) {
          // Refresh the user data from the server
          await this.fetchUser()
        }

        return response.data
      } catch (error) {
        console.error('Email verification error:', error)
        throw error
      }
    },

    async updatePassword (passwordData) {
      if (!this.token) return

      try {
        const response = await axios.put('/api/profile/password', passwordData, {
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        })

        return response.data
      } catch (error) {
        console.error('Password update error:', error)
        throw error
      }
    },

    async verifyMealPlanEmail (verificationCodeOrOptions, email) {
      try {
        const payload = typeof verificationCodeOrOptions === 'object' && verificationCodeOrOptions !== null
          ? verificationCodeOrOptions
          : { verification_code: verificationCodeOrOptions, email }

        const body = {
          email: payload.email ?? email,
        }
        if (payload.token) {
          body.token = payload.token
        }
        if (payload.verification_code ?? payload.verificationCode) {
          body.verification_code = payload.verification_code ?? payload.verificationCode
        }

        const response = await axios.post('/api/verify-meal-plan-email', body)

        // If verification was successful and response contains token, log in automatically
        if (response.data.verified && response.data.token) {
          await this.setToken(response.data.token)
        }
        // If already logged in, just refresh user data
        else if (response.data.verified && this.user && this.token) {
          // Refresh the user data from the server
          await this.fetchUser()
        }

        return response.data
      } catch (error) {
        console.error('Meal plan email verification error:', error)
        throw error
      }
    }
  },

  persist: {
    enabled: true, strategies: [{
      key: 'yumnia-auth', storage: localStorage, paths: ['token', 'user', 'originalUrl'],
    },],
  },
})
